import { useState, useEffect } from "react";
import { FaCheck, FaEdit, FaEye } from "react-icons/fa";
import { RiDeleteBin6Line } from "react-icons/ri";
import ExportOptions from "../ExportOptions";
import PageHeading from "../PageHeading";
import { MdKeyboardArrowLeft, MdKeyboardArrowRight } from "react-icons/md";
import TableSkeleton from "../TableSkeleton";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import ConfirmActionModal from "../ConfirmDeleteModal";
import ViewReturnModal from "./ViewReturnModal";
import { Link, useNavigate } from "react-router-dom";

const InvoiceReturnList = () => {
  const navigate = useNavigate();
  const [allReturns, setAllReturns] = useState([]);
  const [filteredReturns, setFilteredReturns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selectedIds, setSelectedIds] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [deleteIds, setDeleteIds] = useState(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [viewReturn, setViewReturn] = useState(null);
  const limit = 10;
  const API_KEY = process.env.REACT_APP_API_KEY || "caf56e69405fe970f918e99ce86a80fbf0a7d728cca687e8a433b817411a6079";

  const breadcrumbs = [
    { label: "Dashboard", url: "/" },
    { label: "Return"},
    { label: "Return From Customer" },
  ];

  const skeletonColumns = [
    { width: 40, height: 16, isCircle: false, className: "text-left", skeletonClassName: "ml-3" },
    { width: 50, height: 16, isCircle: false, className: "text-left" },
    { width: 120, height: 16, isCircle: false, className: "text-left" },
    { width: 150, height: 16, isCircle: false, className: "text-left" },
    { width: 100, height: 16, isCircle: false, className: "text-right" },
    { width: 100, height: 16, isCircle: false, className: "text-right" },
    { width: 120, height: 16, isCircle: false, className: "text-end" },
  ];

  // Define headers for export
  const headers = ["Sl", "Invoice ID", "Customer Name", "Date", "Total Amount(৳)"];
  const dataMapper = (() => {
  let counter = 0;
  return (returnData) => {
    counter += 1;
    return [
      counter,
      returnData.billNumber || "N/A",
      returnData.customerName || "Unknown",
      new Date(returnData.returnedAt || new Date()).toLocaleDateString(),
      returnData.totalAmount || 0,
    ].join("\t");
  };
})();

  // Toggle dropdown
  const toggleDropdown = () => {
    setDropdownOpen((prev) => !prev);
  };

  const fetchReturns = async () => {
    setLoading(true);
    setError("");
    try {
      const response = await fetch(
        `https://api.muktihospital.com/api/return/customer-returns?limit=1000`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            "x-api-key": API_KEY,
          },
        }
      );

      const data = await response.json();
      // console.log("Fetch customer returns response:", data);
      if (response.ok && data.returns) {
        const stockReturns = data.returns.filter((returnData) => returnData.adjustmentType === "stock");
        setAllReturns(stockReturns);
      } else {
        setError(data.error || `Failed to fetch customer returns (Status: ${response.status})`);
      }
    } catch (err) {
      setError("Unable to connect to the server. Please try again later.");
      console.error("Fetch customer returns error:", err);
    } finally {
      setLoading(false);
    }
  };

  // Apply filters and paginate client-side
  useEffect(() => {
    let filtered = allReturns;

    // Apply search filter
    if (searchQuery) {
      filtered = filtered.filter(
        (returnData) =>
          returnData.billNumber?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          returnData.customerName?.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    // Apply date filter
    if (startDate && endDate) {
      filtered = filtered.filter((returnData) => {
        const returnDate = new Date(returnData.returnedAt);
        const start = new Date(startDate);
        const end = new Date(endDate);
        return returnDate >= start && returnDate <= end;
      });
    }

    // Paginate filtered results
    const startIndex = (currentPage - 1) * limit;
    const paginatedReturns = filtered.slice(startIndex, startIndex + limit);
    setFilteredReturns(paginatedReturns);

    // Update total pages
    const totalFilteredPages = Math.ceil(filtered.length / limit);
    setTotalPages(totalFilteredPages || 1);

    // Reset selected IDs if they are no longer in the filtered list
    setSelectedIds((prev) => prev.filter((id) => filtered.some((r) => r.id === id)));

    // Reset to page 1 if current page exceeds total pages
    if (currentPage > totalFilteredPages && totalFilteredPages > 0) {
      setCurrentPage(1);
    }
  }, [allReturns, searchQuery, startDate, endDate, currentPage]);

  // Fetch data on mount
  useEffect(() => {
    fetchReturns();
  }, []);

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedIds(filteredReturns.map((r) => r.id));
    } else {
      setSelectedIds([]);
    }
  };

  const handleSelect = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((selectedId) => selectedId !== id) : [...prev, id]
    );
  };

  const handleView = (returnData) => {
    setViewReturn(returnData);
  };

  const handleEdit = (id) => {
    navigate(`/edit-return/CustomerReturn/${id}`);
  };

  const handleDelete = (id) => {
    setDeleteIds(id);
    setIsDeleteModalOpen(true);
  };

  const handleBulkDelete = () => {
    if (selectedIds.length === 0) {
      setError("Please select at least one return to delete");
      return;
    }
    setDeleteIds(selectedIds);
    setIsDeleteModalOpen(true);
  };

  const confirmDelete = async (ids) => {
    setLoading(true);
    setError("");
    try {
      const idsToDelete = Array.isArray(ids) ? ids : [ids];
      for (const id of idsToDelete) {
        const response = await fetch(`https://api.muktihospital.com/api/return/CustomerReturn/${id}`, {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
            "x-api-key": API_KEY,
          },
        });

        const data = await response.json();
        if (!response.ok) {
          throw new Error(data.error || `Failed to delete return ID ${id}`);
        }
      }

      setAllReturns((prev) => prev.filter((r) => !idsToDelete.includes(r.id)));
      setSelectedIds([]);

      toast.success(
        idsToDelete.length > 1
          ? `${idsToDelete.length} returns deleted successfully`
          : "Return deleted successfully",
        {
          position: "top-right",
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        }
      );
    } catch (err) {
      setError(err.message || "An error occurred while deleting the returns");
      console.error("Delete error:", err);
      toast.error(`Failed to delete return: ${err.message || "Network error"}`, {
        autoClose: 3000,
      });
    } finally {
      setLoading(false);
      setIsDeleteModalOpen(false);
    }
  };
  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  const calculateTotal = () => {
    return filteredReturns.reduce((sum, returnData) => sum + parseFloat(returnData.totalAmount || 0), 0).toFixed(2);
  };

  return (
    <div>
      <PageHeading title="Return From Customer" breadcrumbs={breadcrumbs} />
      <div className="bg-white shadow-sm rounded-lg">
        <div className="flex items-center justify-between border-b border-dashed border-M-text-color/50 p-4">
          <div className="inline-flex items-center gap-3">
            <h2 className="text-base font-medium text-gray-700 flex items-center gap-2">
              Invoice Return List
            </h2>
            {selectedIds.length > 0 && (
              <button
                className="size-7 bg-M-secondary-color/10 text-M-secondary-color hover:bg-M-secondary-color hover:text-white transition-all duration-300 text-base rounded-md inline-flex items-center justify-center"
                title="Delete Selected"
                onClick={handleBulkDelete}
              >
                <RiDeleteBin6Line />
              </button>
            )}
          </div>
          <div className="flex items-center gap-2">
            <div>
              <input
                type="text"
                className="input-field"
                placeholder="Search By Invoice or Customer"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
            <Link to="/add-return" className="bg-[#73CB64] hover:bg-M-primary-color default-button shrink-0">
              + Add Return
            </Link>
            <ExportOptions
              data={filteredReturns}
              title="Customer Stock Return List"
              headers={headers}
              dataMapper={dataMapper}
              dropdownOpen={dropdownOpen}
              toggleDropdown={toggleDropdown}
            />
          </div>
        </div>

        <div className="pt-4">
          <div className="flex flex-wrap items-center justify-center sm:justify-end gap-2 px-4">
            <div className="flex items-center gap-2">
              <label htmlFor="startDate" className="label mb-0 shrink-0">
                Start Date:
              </label>
              <input
                type="date"
                className="input-field"
                value={startDate}
                id="startDate"
                onChange={(e) => setStartDate(e.target.value)}
              />
            </div>
            <div className="flex items-center gap-2">
              <label htmlFor="endDate" className="label mb-0 shrink-0">
                End Date:
              </label>
              <input
                type="date"
                className="input-field"
                value={endDate}
                id="endDate"
                onChange={(e) => setEndDate(e.target.value)}
              />
            </div>
          </div>

          {error && <p className="text-red-500 px-4 py-2">{error}</p>}

          <div className="mt-4 overflow-x-auto">
            <table className="min-w-[800px] w-full divide-y divide-gray-200 print-table">
              <thead>
                <tr className="px-3 bg-[#FBFBFD]">
                  <th className="text-left">
                    <div className="relative left-3 -top-[1px]">
                      <input
                        type="checkbox"
                        id="agreement"
                        className="hidden peer"
                        checked={selectedIds.length === filteredReturns.length && filteredReturns.length > 0}
                        onChange={handleSelectAll}
                      />
                      <span className="h-4 w-4 border flex-none border-slate-400 rounded inline-flex items-center justify-center ltr:mr-3 rtl:ml-3 transition-all duration-150 bg-slate-100 peer-checked:bg-M-primary-color peer-checked:ring-1 peer-checked:ring-M-primary-color peer-checked:ring-offset-1 absolute top-[6px] left-0 z-0">
                        <FaCheck className="text-slate-100" />
                      </span>
                      <label
                        htmlFor="agreement"
                        className="cursor-pointer font-jost font-normal text-base text-slate-200 relative z-10 pl-6"
                      ></label>
                    </div>
                  </th>
                  <th className="text-left uppercase font-inter font-medium text-sm text-M-heading-color p-3">
                    Sl
                  </th>
                  <th className="text-left uppercase font-inter font-medium text-sm text-M-heading-color p-3">
                    Invoice Id
                  </th>
                  <th className="text-left uppercase font-inter font-medium text-sm text-M-heading-color p-3">
                    Customer Name
                  </th>
                  <th className="text-left uppercase font-inter font-medium text-sm text-M-heading-color p-3">
                    Date
                  </th>
                  <th className="text-right uppercase font-inter font-medium text-sm text-M-heading-color p-3">
                    Total Amount(৳)
                  </th>
                  <th className="text-end uppercase font-inter font-medium text-sm text-M-heading-color p-3 print-hidden-in-print">
                    Action
                  </th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <TableSkeleton rowCount={limit} columns={skeletonColumns} />
                ) : filteredReturns.length > 0 ? (
                  filteredReturns.map((returnData, index) => (
                    <tr key={returnData.id} className="hover:bg-gray-50 border-b">
                      <td className="text-left capitalize font-inter font-medium text-sm text-M-heading-color">
                        <div className="relative left-3 -top-[3px]">
                          <input
                            type="checkbox"
                            id={`item-${returnData.id}`}
                            className="hidden peer"
                            checked={selectedIds.includes(returnData.id)}
                            onChange={() => handleSelect(returnData.id)}
                          />
                          <span className="h-4 w-4 border flex-none border-slate-400 rounded inline-flex items-center justify-center ltr:mr-3 rtl:ml-3 transition-all duration-150 bg-slate-100 peer-checked:bg-M-primary-color peer-checked:ring-1 peer-checked:ring-M-primary-color peer-checked:ring-offset-1 absolute top-[6px] left-0 z-0">
                            <FaCheck className="text-slate-100" />
                          </span>
                          <label
                            htmlFor={`item-${returnData.id}`}
                            className="cursor-pointer font-jost font-normal text-base text-slate-200 relative z-10 pl-6"
                          ></label>
                        </div>
                      </td>
                      <td className="text-left uppercase font-inter font-medium text-sm text-M-heading-color p-3">
                        {(currentPage - 1) * limit + index + 1}
                      </td>
                      <td className="text-left uppercase font-inter font-medium text-sm text-M-heading-color p-3">
                        {returnData.billNumber}
                      </td>
                      <td className="text-left uppercase font-inter font-medium text-sm text-M-heading-color p-3">
                        {returnData.customerName}
                      </td>
                      <td className="text-left uppercase font-inter font-medium text-sm text-M-heading-color p-3">
                        {new Date(returnData.returnedAt).toLocaleDateString()}
                      </td>
                      <td className="text-right capitalize font-inter font-normal text-sm text-M-heading-color py-5 px-3">
                        {returnData.netReturn}
                      </td>
                      <td className="p-3 text-end space-x-2 print-hidden-in-print flex justify-end shrink-0">
                        <button
                          className="size-7 rounded-full bg-M-primary-color/10 text-M-primary-color text-sm inline-flex items-center justify-center hover:bg-M-primary-color hover:text-white transition-all duration-300"
                          title="View"
                          onClick={() => handleView(returnData)}
                        >
                          <FaEye />
                        </button>

                        {/* Primarily hide the edit system */}
                        <button
                          className="size-7 rounded-full bg-M-Green-color/10 text-M-Green-color text-sm inline-flex items-center justify-center hover:bg-M-Green-color hover:text-white transition-all duration-300"
                          title="Edit"
                          onClick={() => handleEdit(returnData.id)}
                        >
                          <FaEdit />
                        </button>
                        <button
                          className="size-7 rounded-full bg-M-secondary-color/10 text-M-secondary-color text-sm inline-flex items-center justify-center hover:bg-M-secondary-color hover:text-white transition-all duration-300"
                          title="Delete"
                          onClick={() => handleDelete(returnData.id)}
                        >
                          <RiDeleteBin6Line />
                        </button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="7" className="text-center p-4 text-gray-500">
                      No customer stock returns found
                    </td>
                  </tr>
                )}
                {!loading && filteredReturns.length > 0 && (
                  <tr>
                    <td
                      className="text-right capitalize font-inter font-bold text-md text-M-heading-color py-5 px-5 border-r"
                      colSpan={5}
                    >
                      Total:{" "}
                    </td>
                    <td className="text-right capitalize font-inter font-bold text-md text-M-heading-color py-5 px-3">
                      {calculateTotal()}
                    </td>
                    <td></td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          {totalPages > 1 && (
            <div className="p-5 text-end border-t border-M-text-color/20">
              <ul className="inline-flex items-center justify-end border-collapse border border-M-text-color/20 rounded-md overflow-hidden">
                <li>
                  <button
                    className="table-pagination-item disabled:opacity-50"
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                  >
                    <MdKeyboardArrowLeft />
                  </button>
                </li>
                {totalPages <= 10
                  ? [...Array(totalPages)].map((_, i) => (
                      <li key={i}>
                        <button
                          className={`table-pagination-item ${
                            currentPage === i + 1 ? "bg-M-primary-color text-white" : ""
                          }`}
                          onClick={() => handlePageChange(i + 1)}
                        >
                          {i + 1}
                        </button>
                      </li>
                    ))
                  : (() => {
                      const pages = [];
                      const firstPages = 5;
                      const lastPages = 3;
                      for (let i = 1; i <= Math.min(firstPages, totalPages); i++) {
                        pages.push(
                          <li key={i}>
                            <button
                              className={`table-pagination-item ${
                                currentPage === i ? "bg-M-primary-color text-white" : ""
                              }`}
                              onClick={() => handlePageChange(i)}
                            >
                              {i}
                            </button>
                          </li>
                        );
                      }
                      if (totalPages > firstPages + lastPages) {
                        pages.push(
                          <li key="ellipsis" className="table-pagination-item">
                            ...
                          </li>
                        );
                      }
                      for (
                        let i = Math.max(firstPages + 1, totalPages - lastPages + 1);
                        i <= totalPages;
                        i++
                      ) {
                        pages.push(
                          <li key={i}>
                            <button
                              className={`table-pagination-item ${
                                currentPage === i ? "bg-M-primary-color text-white" : ""
                              }`}
                              onClick={() => handlePageChange(i)}
                            >
                              {i}
                            </button>
                          </li>
                        );
                      }
                      return pages;
                    })()}
                <li>
                  <button
                    className="table-pagination-item"
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                  >
                    <MdKeyboardArrowRight />
                  </button>
                </li>
              </ul>
            </div>
          )}

          <ViewReturnModal
            isOpen={!!viewReturn}
            onClose={() => setViewReturn(null)}
            returnData={viewReturn}
            onEdit={handleEdit}
            onDelete={confirmDelete}
            type="InvoiceReturn"
          />

          <ConfirmActionModal
            isOpen={isDeleteModalOpen}
            onClose={() => setIsDeleteModalOpen(false)}
            onConfirm={confirmDelete}
            ids={deleteIds}
            entityName="return"
            title="Confirm Deletion"
            confirmText="Delete"
            cancelText="Cancel"
          />
          <ToastContainer />
        </div>
      </div>
    </div>
  );
};

export default InvoiceReturnList;
